import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ArrowUpRight, ChevronDown, ShieldCheck, Waves } from "lucide-react";
import type { AdminAgentDetails } from "../../../shared/admin-agent";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PAGE_SIZE = 12;
const money = (cents: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
const when = (value: string | null) => {
  if (!value) return "Not available";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};
const sourceLabels: Array<[keyof AdminAgentDetails["accessSources"], string]> = [
  ["paidAgent", "Paid Agent"], ["paidPro", "Paid Pro"],
  ["complimentaryAgent", "Complimentary Agent"], ["complimentaryPro", "Complimentary Pro"], ["test", "Test"],
];

async function getDetails(userId: string, offset: number): Promise<AdminAgentDetails> {
  const params = new URLSearchParams({ invoiceOffset: String(offset), invoiceLimit: String(PAGE_SIZE) });
  const response = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/agent-admin?${params}`, {
    credentials: "include", headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message ?? `Agent membership details unavailable (HTTP ${response.status}).`);
  }
  return response.json();
}

export default function AdminAgentDetail({ userId }: { userId: string }) {
  const [invoiceOffset, setInvoiceOffset] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryKey = ["/api/admin/users", userId, "agent-admin", invoiceOffset];
  const query = useQuery<AdminAgentDetails, Error>({
    queryKey, queryFn: () => getDetails(userId, invoiceOffset), retry: false,
  });

  const override = useMutation({
    mutationFn: async (grantAgent: boolean) => {
      const response = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/agent-override`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ grantAgent, confirm: true, reason: reason.trim() }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message ?? `Agent access could not be updated (HTTP ${response.status}).`);
      }
      return response.json().catch(() => ({}));
    },
    onSuccess: (_, grantAgent) => {
      toast({ title: grantAgent ? "Complimentary Agent access granted" : "Complimentary Agent access removed",
        description: "Paid access, other access sources, subscriptions, and alert settings were preserved." });
      setDialogOpen(false);
      setReason("");
      void queryClient.invalidateQueries({ queryKey: ["/api/admin/users", userId, "agent-admin"] });
      void queryClient.invalidateQueries({ queryKey: [`/api/admin/users/${userId}`] });
      void queryClient.invalidateQueries({ queryKey: ["/api/admin/users", userId, "audit"] });
      void queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      void queryClient.invalidateQueries({ queryKey: ["/api/admin/agent-overview"] });
    },
    onError: (error: Error) => toast({ title: "Agent access update failed", description: error.message, variant: "destructive" }),
  });

  if (query.isLoading) return (
    <Card className="border-teal-900/15"><CardContent className="space-y-3 p-5"><div className="h-5 w-44 animate-pulse rounded bg-muted" /><div className="h-20 animate-pulse rounded bg-muted/70" /><div className="h-24 animate-pulse rounded bg-muted/50" /></CardContent></Card>
  );
  if (query.error || !query.data) return (
    <Card className="border-destructive/25"><CardContent className="flex items-start gap-3 p-5 text-sm" role="alert">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
      <div className="flex-1"><p className="font-medium">Agent membership details unavailable</p><p className="mt-1 text-muted-foreground">{query.error?.message ?? "The server returned no membership details."}</p></div>
      <Button size="sm" variant="outline" onClick={() => void query.refetch()}>Retry</Button>
    </CardContent></Card>
  );

  const data = query.data;
  const hasMore = data.invoiceOffset + data.invoices.length < data.invoiceTotal;
  const agentComped = data.accessSources.complimentaryAgent;
  const sourceDisplay = sourceLabels.filter(([key]) => data.accessSources[key]).map(([, label]) => label);
  const invoiceNext = () => {
    const next = invoiceOffset + PAGE_SIZE;
    setInvoiceOffset(next);
    void queryClient.prefetchQuery({ queryKey: ["/api/admin/users", userId, "agent-admin", next], queryFn: () => getDetails(userId, next) });
  };

  return (
    <Card className="overflow-hidden border-teal-900/15">
      <CardHeader className="bg-gradient-to-r from-teal-50/80 to-cyan-50/30 dark:from-teal-950/30 dark:to-cyan-950/10">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Waves className="h-4 w-4 text-teal-700 dark:text-teal-300" /> Agent membership & usage</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">Access sources are listed separately; effective tier reflects their combined result.</p>
          </div>
          <Button size="sm" variant={agentComped ? "outline" : "default"} onClick={() => setDialogOpen(true)}>
            {agentComped ? "Revoke comp Agent" : "Grant comp Agent"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5 p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className="capitalize bg-teal-700 hover:bg-teal-700">{data.effectiveTier} effective tier</Badge>
          {sourceDisplay.length ? sourceDisplay.map(label => <Badge key={label} variant="secondary">{label}</Badge>) : <span className="text-xs text-muted-foreground">No Agent/Pro access source</span>}
          {data.suspended && <Badge variant="destructive">Suspended · access blocked</Badge>}
        </div>
        {data.blockedStates.length > 0 && (
          <div className="rounded-lg border border-amber-700/20 bg-amber-50/70 p-3 dark:bg-amber-950/20">
            <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">Access / sending blocks</p>
            <ul className="mt-1 list-inside list-disc text-xs text-amber-900/80 dark:text-amber-100/80">{data.blockedStates.map((state, index) => <li key={`${state}-${index}`}>{state}</li>)}</ul>
          </div>
        )}
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-lg border p-3">
            <h3 className="text-sm font-semibold">Membership provider</h3>
            {data.membership.error && <p role="alert" className="mt-2 text-xs text-amber-700">{data.membership.error}</p>}
            <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3 text-xs">
              <Detail label="Provider" value={data.membership.provider ?? "None"} />
              <Detail label="Interval" value={data.membership.interval ?? "—"} />
              <Detail label="State" value={`${data.membership.status ?? "—"} · ${data.membership.providerState}`} />
              <Detail label="Cancel at period end" value={data.membership.cancelAtPeriodEnd === null ? "Unavailable" : data.membership.cancelAtPeriodEnd ? "Yes" : "No"} />
              <Detail label="Period start" value={when(data.membership.periodStart)} />
              <Detail label="Period end" value={when(data.membership.periodEnd)} />
            </dl>
            {data.membership.managementUrl && (
              <a href={data.membership.managementUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-primary underline underline-offset-2">
                Provider management <ArrowUpRight className="h-3 w-3" />
              </a>
            )}
          </section>
          <section className="rounded-lg border p-3">
            <h3 className="text-sm font-semibold">Monthly reply allowance</h3>
            <p className="mt-1 text-xs text-muted-foreground">120 included outbound Agent replies per period. Automated alert messages, segments, and inbound member messages are tracked separately.</p>
            <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3 text-xs">
              <Detail label="Included replies" value={data.usage.included.toLocaleString()} />
              <Detail label="Outbound used" value={numberOrUnavailable(data.usage.used)} />
              <Detail label="Remaining" value={numberOrUnavailable(data.usage.remaining)} />
              <Detail label="Pending" value={numberOrUnavailable(data.usage.pendingReplies)} />
              <Detail label="Uncertain" value={numberOrUnavailable(data.usage.uncertainReplies)} />
              <Detail label="Overage replies" value={numberOrUnavailable(data.usage.overageReplies)} />
              <Detail label="Accrued" value={data.usage.accruedCents === null ? "Unavailable" : money(data.usage.accruedCents)} />
              <Detail label="Usage period" value={`${when(data.usage.periodStart)} – ${when(data.usage.periodEnd)}`} />
              <Detail label="Allowance resets" value={when(data.usage.periodEnd)} />
            </dl>
            {data.usage.error && <p role="alert" className="mt-2 text-xs text-amber-700">{data.usage.error}</p>}
          </section>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-lg border p-3">
            <h3 className="text-sm font-semibold">Automated alert messages</h3>
            <p className="mt-1 text-xs text-muted-foreground">Separate from the included 120 outbound Agent replies.</p>
            <dl className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <Detail label="Daily automated texts used" value={`${data.automated.daily.used} / ${data.automated.daily.limit}`} />
              <Detail label="Daily automated texts remaining" value={String(data.automated.daily.remaining)} />
              <Detail label="Monthly billable segments used" value={`${data.automated.used} / ${data.automated.limit}`} />
              <Detail label="Monthly billable segments remaining" value={String(data.automated.remaining)} />
              <Detail label="Active alerts" value={`${data.activeAlerts} / ${data.limits.active}`} />
              <Detail label="Active SMS alert settings" value={String(data.activeSmsAlerts)} />
              <Detail label="Timezone" value={data.automated.daily.timezone ?? "Not set"} />
              <Detail label="Pending timezone" value={data.automated.daily.pendingTimezone ?? "None"} />
              <Detail label="Daily reset" value={when(data.automated.daily.resetAt)} />
              <Detail label="Monthly segment reset (UTC)" value={when(data.automated.resetAt)} />
            </dl>
            {!data.automated.daily.configured && <p className="mt-2 text-xs text-muted-foreground">Daily automated limit is not configured.</p>}
          </section>
          <section className="rounded-lg border p-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-teal-700" /> Reply overage consent · read only</h3>
            <dl className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <Detail label="Consent readiness" value={data.overages.consentCurrent ? "Current" : "Not current"} />
              <Detail label="Consent date" value={when(data.overages.consentAt)} />
              <Detail label="Charging operational" value={yesNo(data.overages.operational)} />
              <Detail label="Charging enabled" value={yesNo(data.overages.chargingEnabled)} />
              <Detail label="Overages enabled" value={yesNo(data.overages.enabled)} />
              <Detail label="Payment method saved" value={yesNo(data.overages.paymentMethodSaved)} />
              <Detail label="Rate" value={money(data.overages.rateCents)} />
              <Detail label="Customer spending cap" value={money(data.overages.capCents)} />
              <Detail label="Included replies exhausted" value={data.overages.exhausted === null ? "Unavailable" : yesNo(data.overages.exhausted)} />
            </dl>
            {data.overages.blockedReason && <p className="mt-2 rounded bg-muted/50 p-2 text-xs text-muted-foreground">Blocked: {data.overages.blockedReason}</p>}
          </section>
        </div>
        <section className="rounded-lg border">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2.5">
            <div><h3 className="text-sm font-semibold">Dated reply invoices</h3><p className="text-xs text-muted-foreground">{data.invoiceTotal.toLocaleString()} total · page of {PAGE_SIZE}</p></div>
            {data.invoiceTotal > PAGE_SIZE && <span className="text-xs text-muted-foreground">Showing {data.invoiceOffset + 1}–{data.invoiceOffset + data.invoices.length}</span>}
          </div>
          {data.invoices.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No invoices recorded for this account.</p>
          ) : (
            <div className="divide-y">
              {data.invoices.map(invoice => <div key={invoice.id} className="grid gap-2 px-3 py-3 text-xs sm:grid-cols-[1fr_auto]">
                <div><p className="font-medium">{invoice.status} · {money(invoice.amountCents)} · {invoice.replies.toLocaleString()} replies</p>
                  <p className="mt-1 text-muted-foreground">Period {when(invoice.periodStart)} — {when(invoice.periodEnd)}</p>
                  <p className="mt-0.5 text-muted-foreground">Created {when(invoice.createdAt)} · updated {when(invoice.updatedAt)}</p>
                  {invoice.error && <p role="alert" className="mt-1 text-amber-700">Provider error: {invoice.error}</p>}
                </div>
                <div className="break-all text-muted-foreground sm:text-right"><p>Invoice #{invoice.id}</p><p>{invoice.providerId ? `Provider ${invoice.providerId}` : "Provider ID unavailable"}</p></div>
              </div>)}
            </div>
          )}
          {(invoiceOffset > 0 || hasMore) && (
            <div className="flex flex-wrap justify-between gap-2 border-t p-3">
              <Button variant="outline" size="sm" disabled={invoiceOffset <= 0} onClick={() => setInvoiceOffset(Math.max(0, invoiceOffset - PAGE_SIZE))}>Newer invoices</Button>
              {hasMore && <Button variant="outline" size="sm" onClick={invoiceNext}>Older invoices <ChevronDown className="ml-2 h-4 w-4" /></Button>}
            </div>
          )}
        </section>
      </CardContent>
      <Dialog open={dialogOpen} onOpenChange={open => { if (!override.isPending) setDialogOpen(open); }}>
          <DialogContent className="max-h-[80dvh] overflow-y-auto sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{agentComped ? "Revoke complimentary Agent access?" : "Grant complimentary Agent access?"}</DialogTitle>
              <DialogDescription>Only the complimentary Agent source changes. Paid Agent/Pro, complimentary Pro, test access, subscriptions, and alert settings are preserved.</DialogDescription>
            </DialogHeader>
            <div className="mt-4 rounded-lg border border-teal-800/15 bg-teal-50/60 p-3 text-xs text-teal-950 dark:bg-teal-950/20 dark:text-teal-100">
              Effective access is recalculated from all remaining sources. This action does not change billing or cancel a subscription.
            </div>
            <div className="mt-4 space-y-2">
              <Label htmlFor="agent-override-reason">Reason <span className="text-destructive">*</span></Label>
              <Input id="agent-override-reason" value={reason} onChange={event => setReason(event.target.value)} placeholder="Why is this access changing?" maxLength={500} disabled={override.isPending} />
              <p className="text-xs text-muted-foreground">Required for the admin record. {reason.trim().length}/500</p>
            </div>
            {override.error && <p role="alert" className="mt-3 text-sm text-destructive">{override.error.message}</p>}
            <DialogFooter className="mt-2">
              <Button variant="outline" disabled={override.isPending} onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button disabled={!reason.trim() || override.isPending} onClick={() => override.mutate(!agentComped)}>
                {override.isPending ? "Saving…" : agentComped ? "Confirm revoke" : "Confirm grant"}
              </Button>
            </DialogFooter>
          </DialogContent>
      </Dialog>
    </Card>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return <div className="min-w-0"><dt className="text-muted-foreground">{label}</dt><dd className="mt-0.5 break-words font-medium">{value}</dd></div>;
}
function numberOrUnavailable(value: number | null) { return value === null ? "Unavailable" : value.toLocaleString(); }
function yesNo(value: boolean) { return value ? "Yes" : "No"; }
